import { css } from '@emotion/react';
import {
  type FC, useMemo, useState,
} from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import {
  type DropdownOption,
  RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { CONFIG } from '~/_shared/constants/config';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { DEFAULT_RANGES_COUNT } from '~/boundary/settings/useBoundaryFillSettingsPropsVariants';
import { useIsBoundaryToolReady } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useIsBoundaryToolReady';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  BoundaryTerritoryType, type CalculateBucketFunctionNumeric,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { ActiveBoundaryFillEditComponent } from '../../activeBoundary/edit/activeBoundaryFillEditComponent';
import { useActiveBoundaryFillEdit } from '../../activeBoundary/edit/useActiveBoundaryFillEdit';
import { BoundarySelectedOption } from '../../fill/boundaryFill.enums';
import { LoadBoundaryPaneSubmitButtonComponent } from '../loadBoundaryPaneSubmitButton/boundaryCreateSubmitButton.component';
import {
  getCustomBoundaryFillTypeOptions,
  LoadCustomBoundaryFillType, trimOptimizedTerritoryGroupNameMadeForTesting,
} from './loadCustomBoundaryGroupPane.helpers';

const panelDropdownSectionStyle = css({
  marginBottom: 8,
});

const labelStyle = css({
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '4px',
  textTransform: 'uppercase',
});

const addBoundaryTooltipStyle = css({
  display: 'block',
});

type LoadCustomBoundaryGroupDataWithBoundaryFillType = Readonly<{
  boundaryGroupId: number;
  boundaryFillType: LoadCustomBoundaryFillType.DefaultFill | LoadCustomBoundaryFillType.NoTerritoryFill | LoadCustomBoundaryFillType.RandomizedFill;
}>;

type LoadCustomBoundaryGroupDataBasedOnFillData = Readonly<{
  boundaryGroupId: number;
  boundaryFillType: LoadCustomBoundaryFillType.FillBasedOnData;
  boundaryTerritoryType: BoundaryTerritoryType;
  boundaryFillData: SpreadsheetColumnId | null;
  numericCalculateBucketFunction: CalculateBucketFunctionNumeric | null;
}>;

export type LoadCustomBoundaryGroupData = LoadCustomBoundaryGroupDataWithBoundaryFillType | LoadCustomBoundaryGroupDataBasedOnFillData;

type LoadCustomBoundaryGroupPaneComponentProps = Readonly<{
  customBoundaryGroups: ReadonlyArray<BoundaryGroup>;
  onAddBoundary: (results: LoadCustomBoundaryGroupData) => void;
  isBoundaryGroupUsedOnMap: (boundaryGroupId: BoundaryGroupId) => boolean;
}>;

const excludedBoundaryTerritoryTypes = [BoundaryTerritoryType.Manual];

export const LoadCustomBoundaryGroupPaneComponent: FC<LoadCustomBoundaryGroupPaneComponentProps> = props => {
  const [boundaryGroupId, setBoundaryGroupId] = useState<number | null>(null);
  const [boundaryFillType, setBoundaryFillType] = useState<LoadCustomBoundaryFillType>(LoadCustomBoundaryFillType.DefaultFill);
  const [t] = useTranslation();

  const { isBoundaryToolPreparing, boundaryToolDisabledReason } = useIsBoundaryToolReady();

  const {
    isUpdateDisabled: isFillByDataUpdateDisabled, boundaryFillModel, ...boundaryFillProps
  } = useActiveBoundaryFillEdit({
    boundaryGroupId,
    fixedRangeCount: DEFAULT_RANGES_COUNT,
    hiddenBoundaryTerritoryTypeOptions: excludedBoundaryTerritoryTypes,
  });

  const { isBoundaryGroupUsedOnMap } = props;

  const isBoundaryGroupAlreadyUsed = useMemo(() =>
    boundaryGroupId === null ? false : isBoundaryGroupUsedOnMap(boundaryGroupId),
  [isBoundaryGroupUsedOnMap, boundaryGroupId]);

  const customBoundaryGroupsListOptions: DropdownOption<number>[] = useMemo(() =>
    props.customBoundaryGroups
      .filter((group) => !group.wms.wmsTemporary && !group.mot.motTemporary)
      .map(item => ({
        name: CONFIG.ENABLE_OPTIMIZED_TERRITORY_AUTO_NAME_BUILDER
          ? trimOptimizedTerritoryGroupNameMadeForTesting(item.name)
          : item.name,
        value: item.id,
      })
      ), [props.customBoundaryGroups]);

  const hasValidOptions = customBoundaryGroupsListOptions.length > 0;

  const boundaryFillTypeOptions = useMemo(() => getCustomBoundaryFillTypeOptions(t), [t]);

  const isAddCustomBoundaryGroupDisabled = isBoundaryToolPreparing || isBoundaryGroupAlreadyUsed || !boundaryGroupId
    || ((boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData) && isFillByDataUpdateDisabled);

  const addCustomBoundaryGroupsDisabledReason = isBoundaryGroupAlreadyUsed
    ? t('Territory Group is already loaded')
    : boundaryToolDisabledReason;

  const onSubmit = () => {
    if (boundaryGroupId === null) {
      return;
    }

    if (boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData) {
      if (boundaryFillModel.boundaryTerritoryType === null) {
        return;
      }
      props.onAddBoundary({
        boundaryGroupId,
        boundaryFillType: LoadCustomBoundaryFillType.FillBasedOnData,
        boundaryTerritoryType: boundaryFillModel.boundaryTerritoryType,
        boundaryFillData: boundaryFillModel.boundaryFillData,
        numericCalculateBucketFunction: boundaryFillModel.numericCalculateBucketFunction,
      });
    }
    else {
      props.onAddBoundary({
        boundaryGroupId,
        boundaryFillType,
      });
    }
  };

  let labelCount = 2;

  return (
    <div>
      <div css={panelDropdownSectionStyle}>
        <div css={labelStyle}>
          {labelCount++}. {t(BoundarySelectedOption.SelectCustomBoundaryGroup)}
        </div>

        <AutocompleteComponent
          onChange={setBoundaryGroupId}
          options={customBoundaryGroupsListOptions}
          placeholder={hasValidOptions ? t('Type to search...') : t('No territory groups found')}
          value={boundaryGroupId}
          isDisabled={!hasValidOptions}
          isClearable
        />
      </div>

      {boundaryGroupId !== null && (
        <>
          <div css={panelDropdownSectionStyle}>
            <div css={labelStyle}>
              {labelCount++}. {t(BoundarySelectedOption.BoundaryFillType)}
            </div>

            <RegularDropdownComponent
              onChange={setBoundaryFillType}
              options={boundaryFillTypeOptions}
              placeholder={t(BoundarySelectedOption.BoundaryFillType)}
              value={boundaryFillType}
            />
          </div>

          {boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData && (
            <ActiveBoundaryFillEditComponent
              labelCountStart={labelCount++}
              {...boundaryFillModel}
              {...boundaryFillProps}
            />
          )}
        </>
      )}

      <TooltipComponent
        tooltipContent={addCustomBoundaryGroupsDisabledReason}
        placement={TooltipPlacement.Bottom}
      >
        <div css={addBoundaryTooltipStyle}>
          <LoadBoundaryPaneSubmitButtonComponent
            text={t('Load Territories')}
            isDisabled={isAddCustomBoundaryGroupDisabled}
            onClick={onSubmit}
          />
        </div>
      </TooltipComponent>
    </div>
  );
};
